/**
 * 🌟 SOUL TAB CONTAINER - Architecture Mozart/Jobs/Tesla
 * 
 * Conteneur pour l'onglet Mon Âme (Soul + Psyche) dans Heartbeats.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Brain, Heart, Star } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent } from '../../ui/dialog';
import { useSoul } from '../../../hooks/heartbeats';
import { SoulAspirationsQuiz } from '../features/soul';
import { PsycheQuiz } from '../features/psyche';
import { PixieAvatar } from '../PixieAvatar';

export const SoulTab = ({ customerId, isDarkMode }) => {
  const soul = useSoul(customerId);
  const [showSoulQuiz, setShowSoulQuiz] = useState(false);
  const [showPsycheQuiz, setShowPsycheQuiz] = useState(false);
  const [quizStatus, setQuizStatus] = useState(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    const status = await soul.getQuizStatus();
    setQuizStatus(status);
  };

  const quizOptions = [
    {
      id: 'soul-aspirations',
      title: 'Tes Aspirations',
      description: 'Rêves, valeurs, croissance personnelle',
      emoji: '✨',
      icon: Star,
      color: 'from-amber-500 to-orange-500',
      duration: '~2 min',
      completed: quizStatus?.hasCompletedSoul,
      onClick: () => setShowSoulQuiz(true)
    },
    {
      id: 'psyche-scenarios',
      title: 'Scénarios de vie',
      description: 'Petites questions pour mieux te connaître',
      emoji: '🧠',
      icon: Brain,
      color: 'from-violet-500 to-purple-500',
      duration: '~3 min',
      completed: quizStatus?.hasCompletedPsyche,
      onClick: () => setShowPsycheQuiz(true)
    }
  ];

  return (
    <div className={`p-4 space-y-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="text-4xl mb-2">🧬</div>
        <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Connais-toi toi-même
        </h2>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Plus tu te connais, mieux on te matche ! ✨
        </p>
      </div>

      {/* Quiz Cards */}
      <div className="space-y-4">
        {quizOptions.map((quiz, index) => (
          <motion.div
            key={quiz.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.15 }}
          >
            <button
              onClick={quiz.onClick}
              className={`w-full p-4 rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98] ${
                isDarkMode 
                  ? 'bg-gray-800/50 border border-gray-700 hover:border-purple-500/50' 
                  : 'bg-white border border-gray-200 hover:border-purple-300 shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${quiz.color} flex items-center justify-center text-white text-2xl shadow-lg flex-shrink-0`}>
                  {quiz.emoji}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {quiz.title}
                    </h3>
                    {quiz.completed && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-600">
                        ✓ Fait
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {quiz.description}
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    ⏱️ {quiz.duration}
                  </p>
                </div>

                {/* Arrow */}
                <div className={`text-2xl ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  ›
                </div>
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      {/* Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className={`p-4 rounded-xl ${
          isDarkMode ? 'bg-purple-900/20 border border-purple-700/30' : 'bg-purple-50 border border-purple-200'
        }`}
      >
        <div className="flex items-start gap-3">
          <PixieAvatar size="md" />
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
              Pixie utilise ces infos pour...
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-purple-400/70' : 'text-purple-600/70'}`}>
              Te trouver des personnes qui te correspondent vraiment, pas juste physiquement ! 💜
            </p>
          </div>
        </div>
      </motion.div>

      {/* Soul Quiz Modal */}
      <Dialog open={showSoulQuiz} onOpenChange={setShowSoulQuiz}>
        <DialogContent className={`max-w-md mx-auto p-0 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <SoulAspirationsQuiz
            customerId={customerId}
            isDarkMode={isDarkMode}
            onComplete={() => {
              setShowSoulQuiz(false);
              loadStatus();
            }}
            onSkip={() => setShowSoulQuiz(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Psyche Quiz Modal */}
      <Dialog open={showPsycheQuiz} onOpenChange={setShowPsycheQuiz}>
        <DialogContent className={`max-w-md mx-auto p-0 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <PsycheQuiz
            customerId={customerId}
            isDarkMode={isDarkMode}
            onComplete={() => {
              setShowPsycheQuiz(false);
              loadStatus();
            }}
            onSkip={() => setShowPsycheQuiz(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SoulTab;
